import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Virtuoso, type VirtuosoHandle } from "react-virtuoso";
import { useAppData, type Domain, type Project } from "store/appData";
import { useDatasetDetail } from "./DatasetDetailContext";
import {
  formatProjectLabel,
  useDatasetViewerSettings,
} from "../hooks/useDatasetViewerSettings";
import { type DatasetImage } from "../api";
import t from "../translations";

interface AnnotationTarget {
  url: string;
  domainName: string;
}

function resolveAnnotationTarget(
  image: DatasetImage,
  projects: Record<number, Project>,
  domains: Record<number, Domain>
): AnnotationTarget | null {
  const project = projects[image.project_id];
  if (!project?.primary_domain_id) return null;
  const domain = domains[project.primary_domain_id];
  if (!domain) return null;
  const trimmed = domain.url.replace(/\/+$/, "");
  const withScheme = /^https?:\/\//.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;
  return {
    url: `${withScheme}/${image.project_id}/${image.id}`,
    domainName: domain.name,
  };
}

export default function DatasetImageList() {
  const params = useParams<{ datasetId: string; imageId: string }>();
  const datasetId = params.datasetId ?? "";
  const activeImageId = params.imageId ? parseInt(params.imageId) : -1;

  const { projects, domains } = useAppData();
  const { projectNameDisplay } = useDatasetViewerSettings();
  const {
    datasetName,
    totalImageCount,
    projectCounts,
    images,
    loadMore,
    hasMore,
    detailBasePath,
    kind,
  } = useDatasetDetail();

  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const navigate = useNavigate();
  const [projectsExpanded, setProjectsExpanded] = useState(false);

  const projectLabelMap = useMemo(
    () =>
      new Map(
        Object.values(projects).map((p) => [
          p.id,
          formatProjectLabel(p, projectNameDisplay),
        ])
      ),
    [projects, projectNameDisplay]
  );

  useEffect(() => {
    if (activeImageId >= 0 || !images.length) return;
    navigate(`${detailBasePath}/${datasetId}/${images[0].id}`, {
      replace: true,
    });
  }, [activeImageId, images, navigate, detailBasePath, datasetId]);

  useEffect(() => {
    if (activeImageId < 0 || !images.length) return;
    const index = images.findIndex((img) => img.id === activeImageId);
    if (index >= 0) {
      virtuosoRef.current?.scrollToIndex({ index, align: "center" });
    }
  }, [activeImageId, images]);

  useEffect(() => {
    if (activeImageId < 0 || !images.length) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const target = event.target as HTMLElement | null;
      if (
        target?.isContentEditable ||
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.tagName === "SELECT"
      ) {
        return;
      }

      const index = images.findIndex((img) => img.id === activeImageId);
      if (index < 0) return;

      if (event.key === "ArrowDown") {
        event.preventDefault();
        const next = images[index + 1];
        if (next) {
          navigate(`${detailBasePath}/${datasetId}/${next.id}`);
        } else if (hasMore) {
          loadMore();
        }
      } else {
        event.preventDefault();
        const prev = images[index - 1];
        if (prev) {
          navigate(`${detailBasePath}/${datasetId}/${prev.id}`);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    activeImageId,
    images,
    navigate,
    detailBasePath,
    datasetId,
    hasMore,
    loadMore,
  ]);

  const handleRowClick = (image: DatasetImage) => {
    navigate(`${detailBasePath}/${datasetId}/${image.id}`);
  };

  const hasProjects = projectCounts.length > 0;
  const imageCountText = datasetName
    ? t.ImageCount(totalImageCount)
    : t.LoadingImages;
  const projectCountText = t.ProjectCount(projectCounts.length);

  return (
    <Stack
      sx={{
        height: "100%",
        width: 320,
        flexShrink: 0,
        bgcolor: "background.paper",
        borderRight: 1,
        borderColor: "divider",
      }}
      data-testid="datasetImageList.container"
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Stack
          sx={{
            flexDirection: "row",
            alignItems: "center",
            gap: 0.5,
            cursor: hasProjects ? "pointer" : "default",
            userSelect: "none",
            color: "text.secondary",
          }}
          onClick={() => hasProjects && setProjectsExpanded((prev) => !prev)}
          data-testid="datasetImageList.projectCountToggle"
        >
          <Typography variant="caption">{imageCountText}</Typography>
          {hasProjects && (
            <>
              <Typography variant="caption">·</Typography>
              <Typography variant="caption">{projectCountText}</Typography>
              {projectsExpanded ? (
                <ExpandLessIcon fontSize="inherit" />
              ) : (
                <ExpandMoreIcon fontSize="inherit" />
              )}
            </>
          )}
        </Stack>
        {hasProjects && projectsExpanded && (
          <Box
            sx={{ mt: 0.5, pt: 0.5, maxHeight: "50vh", overflowY: "auto" }}
            data-testid="datasetImageList.projectBreakdown"
          >
            {projectCounts.map(({ id, count }, index) => {
              const pct =
                totalImageCount > 0
                  ? Math.round((count / totalImageCount) * 100)
                  : 0;
              return (
                <Stack
                  key={id}
                  sx={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    gap: 1,
                    px: 0.5,
                    py: 0.25,
                    fontSize: 11,
                    color: "text.secondary",
                    bgcolor: index % 2 === 0 ? "action.hover" : "transparent",
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {projectLabelMap.get(id) ?? `#${id}`}
                  </Box>
                  <Stack
                    sx={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <Box component="span">{count.toLocaleString()}</Box>
                    <Box
                      component="span"
                      sx={{
                        color: "text.disabled",
                        fontSize: 10,
                        width: 32,
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {pct}%
                    </Box>
                  </Stack>
                </Stack>
              );
            })}
          </Box>
        )}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        <Virtuoso
          ref={virtuosoRef}
          style={{ width: "100%", height: "100%" }}
          data={images}
          endReached={loadMore}
          itemContent={(_, image) => {
            const active = image.id === activeImageId;
            const annotation = resolveAnnotationTarget(image, projects, domains);
            return (
              <Stack
                sx={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 2,
                  py: 0.625,
                  mb: "1px",
                  cursor: active ? "default" : "pointer",
                  bgcolor: active ? "primary.main" : "transparent",
                  color: active ? "primary.contrastText" : "text.primary",
                  fontWeight: active ? 700 : 400,
                  "&:hover": active ? undefined : { bgcolor: "action.hover" },
                }}
                role={active ? undefined : "button"}
                tabIndex={active ? -1 : 0}
                onClick={() => !active && handleRowClick(image)}
                onKeyDown={(e) => {
                  if (active) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleRowClick(image);
                  }
                }}
                data-testid={`datasetImageList.row.${kind}.${image.id}`}
                {...(active && { "data-active": "" })}
              >
                <Stack
                  sx={{ flexDirection: "row", alignItems: "center", gap: 1 }}
                >
                  <ImageIcon
                    fontSize="small"
                    sx={{ color: active ? "common.white" : "grey.500" }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: "inherit" }}>
                    {`${image.region.toUpperCase()}${image.id}`}
                  </Typography>
                </Stack>
                <Stack
                  sx={{ flexDirection: "row", alignItems: "center", gap: 1 }}
                >
                  <Typography variant="caption" sx={{ color: "grey.400" }}>
                    {projectLabelMap.get(image.project_id) ??
                      `#${image.project_id}`}
                  </Typography>
                  <Tooltip
                    title={
                      annotation
                        ? t.OpenInAnnotationToolOn(annotation.domainName)
                        : t.AnnotationToolUnavailable
                    }
                    arrow
                  >
                    <Box component="span" onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        component="a"
                        size="small"
                        href={annotation?.url ?? undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        disabled={annotation === null}
                        data-testid={`datasetImageList.openAnnotationTool.${image.id}`}
                        sx={{ color: "inherit" }}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Tooltip>
                </Stack>
              </Stack>
            );
          }}
        />
      </Box>
    </Stack>
  );
}
